import { useState, useRef, useMemo, useCallback } from 'react';

import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';

import { MotionItem } from './AnimatedGroup';
import { getComponentByType } from '../utils/componentRegistry';
import { ComponentTypes } from '../utils/constants';
import shouldHideComponent from '../utils/shouldHideComponent';

/************************************************
 * InstructionGroup – Instruction rendering logic
 * ------------------------------------------------
 *  ‣ Contains business logic for button hiding
 *  ‣ Handles UserResponse detection
 *  ‣ Component instantiation and filtering
 ************************************************/

// Constants
const FALLBACK_DELAYS = {
  [ComponentTypes.MESSAGE]: 300,
  [ComponentTypes.BUTTON_PROMPT]: 200,
  [ComponentTypes.USER_RESPONSE]: 200,
  DEFAULT: 300
};

const CALLBACK_SUPPORTED_TYPES = [ComponentTypes.CHECK_IN, ComponentTypes.TYPING_EFFECT];

/**
 * Check if component type supports onAnimationComplete callback
 * @param {string} componentType - The component type to check
 * @returns {boolean} Whether component supports callback
 */
function componentSupportsCallback(componentType) {
  return CALLBACK_SUPPORTED_TYPES.includes(componentType);
}

/**
 * Generate unique animation key for tracking completed animations
 * @param {string|undefined} instructionId - The instruction ID
 * @param {string|undefined} attributesId - The attributes ID
 * @param {number} index - The item index
 * @returns {string} Unique animation key
 */
function getAnimationKey(instructionId, attributesId, index) {
  return `${instructionId || attributesId || 'unknown'}-${index}`;
}

function getUiInstructions(instruction) {
  if (!instruction) return [];

  return (
    instruction.attributes?.uiInstructions ||
    [instruction.attributes?.uiInstruction].filter(Boolean)
  );
}

/**
 * Renders a group of UI instructions with button hiding logic
 * @param {object} props - Component props
 * @param {object} props.instruction - Current instruction group
 * @param {Function} props.onComponentClick - Click handler for components
 * @param {number} props.currentIndex - Current instruction group index
 * @param {object} props.childVariant - Animation variants for child items
 * @param {string} props.currentMode - Current mode ('get' or 'post') for animation control
 * @param {boolean} props.isNewComponent - Whether this is a new component that should animate
 * @param {number} props.latestTextUserResponseIndex - Latest text UserResponse index
 * @param {number} props.latestUserResponseIndex - Latest UserResponse index
 * @param {Array} props.nextInstruction - Next instruction group
 * @returns {Array} Array of rendered instruction components
 */
function InstructionGroup({
  instruction,
  childVariant = 'fade',
  currentIndex = 0,
  currentMode,
  isNewComponent = false,
  latestTextUserResponseIndex,
  latestUserResponseIndex,
  nextInstruction,
  onComponentClick
}) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const uiInstructions = getUiInstructions(instruction);

  const shouldAnimate = (currentMode === 'post' && isNewComponent) || currentMode === 'initial';

  // Refs for tracking animation state
  const completedAnimationsRef = useRef(new Set());
  const renderedItemsRef = useRef(new Set());

  // Memoize filtered instructions
  const filteredInstructions = useMemo(
    () =>
      uiInstructions.filter(
        (uiInstruction) =>
          !shouldHideComponent(
            uiInstruction,
            uiInstructions,
            currentIndex,
            latestTextUserResponseIndex,
            latestUserResponseIndex,
            getUiInstructions(nextInstruction)
          )
      ),
    [
      uiInstructions,
      currentIndex,
      latestTextUserResponseIndex,
      latestUserResponseIndex,
      nextInstruction
    ]
  );

  // Sequential animation control
  const [currentAnimatingIndex, setCurrentAnimatingIndex] = useState(
    shouldAnimate ? 0 : filteredInstructions.length
  );

  // Animation completion handler
  const handleAnimationComplete = useCallback(
    (index) => {
      const animationKey = getAnimationKey(instruction.id, instruction.attributes?.id, index);

      if (completedAnimationsRef.current.has(animationKey)) {
        return;
      }

      if (
        shouldAnimate &&
        index === currentAnimatingIndex &&
        index < filteredInstructions.length - 1
      ) {
        completedAnimationsRef.current.add(animationKey);
        setCurrentAnimatingIndex((prev) => prev + 1);
      }
    },
    [
      shouldAnimate,
      currentAnimatingIndex,
      filteredInstructions.length,
      instruction.id,
      instruction.attributes?.id
    ]
  );

  // Fallback handler for components without onAnimationComplete
  const handleMotionItemComplete = useCallback(
    (index, componentType) => {
      if (componentSupportsCallback(componentType)) {
        return;
      }

      const fallbackDelay = FALLBACK_DELAYS[componentType] || FALLBACK_DELAYS.DEFAULT;

      setTimeout(() => {
        const animationKey = getAnimationKey(instruction.id, instruction.attributes?.id, index);
        if (!completedAnimationsRef.current.has(animationKey)) {
          handleAnimationComplete(index);
        }
      }, fallbackDelay);
    },
    [handleAnimationComplete, instruction.id, instruction.attributes?.id]
  );

  // Determine items to render for sequential animation
  const itemsToRender = shouldAnimate
    ? filteredInstructions.slice(0, currentAnimatingIndex + 1)
    : filteredInstructions;

  return itemsToRender.map((uiInstruction, index) => {
    const Component = getComponentByType(uiInstruction?.componentType, DEPLOY_ENV);

    if (!Component) {
      return null;
    }

    const itemKey = getAnimationKey(instruction.id, instruction.attributes?.id, index);

    // Track rendered items to prevent re-animation
    const hasBeenRendered = renderedItemsRef.current.has(itemKey);
    if (!hasBeenRendered) {
      renderedItemsRef.current.add(itemKey);
    }

    const supportsCallback = componentSupportsCallback(uiInstruction.componentType);
    const isCurrentlyAnimating = shouldAnimate && index === currentAnimatingIndex;

    return (
      <MotionItem
        key={itemKey}
        animate="visible"
        custom={index}
        exit="hidden"
        initial={shouldAnimate && !hasBeenRendered ? 'hidden' : 'visible'}
        variants={childVariant}
        onAnimationComplete={
          isCurrentlyAnimating && !supportsCallback
            ? () => handleMotionItemComplete(index, uiInstruction.componentType)
            : undefined
        }
      >
        <Component
          {...uiInstruction.props}
          instructionId={instruction.id || instruction.attributes?.id}
          shouldAnimate={shouldAnimate}
          threadUuid={instruction.attributes?.threadUuid}
          onAnimationComplete={
            isCurrentlyAnimating && supportsCallback
              ? () => handleAnimationComplete(index)
              : undefined
          }
          onComponentClick={onComponentClick}
        />
      </MotionItem>
    );
  });
}

InstructionGroup.propTypes = {
  childVariant: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  currentIndex: PropTypes.number,
  currentMode: PropTypes.oneOf(['get', 'post', 'initial', null]),
  instruction: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.shape({
      id: PropTypes.string,
      uiInstruction: PropTypes.object,
      uiInstructions: PropTypes.arrayOf(PropTypes.object),
      threadUuid: PropTypes.string
    })
  }).isRequired,
  isLastGroup: PropTypes.bool,
  isNewComponent: PropTypes.bool,
  latestTextUserResponseIndex: PropTypes.number,
  latestUserResponseIndex: PropTypes.number,
  onComponentClick: PropTypes.func
};

export default InstructionGroup;
